/** Small hooks for live Firestore collection/document subscriptions. */
import {
  DocumentData,
  Query,
  QueryConstraint,
  QueryDocumentSnapshot,
  collection,
  doc,
  getCountFromServer,
  limit as fbLimit,
  onSnapshot,
  orderBy,
  query,
  startAfter,
} from 'firebase/firestore';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { db } from '../firebase';

export interface WithId {
  id: string;
}

/**
 * Subscribe to a live collection query. The caller passes the constraints array
 * and an explicit `deps` of the values that vary (e.g. a status filter), because
 * QueryConstraint exposes only its kind ("where"/"orderBy"), never its field or
 * value — so the subscription must be re-keyed on the caller's actual inputs.
 */
export function useCollection<T extends WithId>(
  path: string | null,
  constraints: QueryConstraint[] = [],
  deps: ReadonlyArray<unknown> = [],
): { data: T[]; loading: boolean; error: string | null } {
  const [data, setData] = useState<T[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const depsKey = JSON.stringify(deps);
  const key = useMemo(() => `${path}::${depsKey}`, [path, depsKey]);

  useEffect(() => {
    if (!path) {
      setData([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    const q: Query<DocumentData> = query(collection(db, path), ...constraints);
    const unsub = onSnapshot(
      q,
      (snap) => {
        setData(snap.docs.map((d) => ({ id: d.id, ...(d.data() as object) }) as T));
        setLoading(false);
        setError(null);
      },
      (err) => {
        setError(err.message);
        setLoading(false);
      },
    );
    return unsub;
    // constraints are re-applied whenever `key` (path + caller deps) changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  return { data, loading, error };
}

export interface PagedOptions {
  orderField: string;
  dir?: 'asc' | 'desc';
  pageSize?: number;
  /** where(...) filters only (no orderBy/limit). */
  filters?: QueryConstraint[];
  /** Values the filters depend on, so the query re-keys when they change. */
  deps?: ReadonlyArray<unknown>;
}

export interface PagedResult<T> {
  items: T[];
  page: number; // 0-based
  pageSize: number;
  total: number | null;
  loading: boolean;
  error: string | null;
  hasPrev: boolean;
  hasNext: boolean;
  next: () => void;
  prev: () => void;
}

/**
 * Cursor-paginated, live collection view: subscribes to one page at a time (so
 * reads stay bounded as history grows) and reports the total via a count
 * aggregate. Forward/back navigation uses Firestore startAfter cursors.
 */
export function usePagedCollection<T extends WithId>(path: string, opts: PagedOptions): PagedResult<T> {
  const { orderField, dir = 'desc', pageSize = 20, filters = [], deps = [] } = opts;
  const depsKey = JSON.stringify(deps);
  const [page, setPage] = useState(0);
  const [items, setItems] = useState<T[]>([]);
  const [total, setTotal] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // cursors[i] = the startAfter doc for page i (cursors[0] is undefined).
  const cursors = useRef<Array<QueryDocumentSnapshot<DocumentData> | undefined>>([undefined]);

  // Reset to page 0 whenever the query identity changes.
  useEffect(() => {
    cursors.current = [undefined];
    setPage(0);
  }, [path, depsKey, orderField, dir, pageSize]);

  // Total count (one-shot per query identity).
  useEffect(() => {
    let alive = true;
    getCountFromServer(query(collection(db, path), ...filters))
      .then((s) => alive && setTotal(s.data().count))
      .catch(() => alive && setTotal(null));
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [path, depsKey]);

  // Live subscription to the current page.
  useEffect(() => {
    setLoading(true);
    const cur = cursors.current[page];
    const cons: QueryConstraint[] = [...filters, orderBy(orderField, dir), fbLimit(pageSize)];
    if (cur) cons.push(startAfter(cur));
    const unsub = onSnapshot(
      query(collection(db, path), ...cons),
      (snap) => {
        setItems(snap.docs.map((d) => ({ id: d.id, ...(d.data() as object) }) as T));
        cursors.current[page + 1] = snap.docs[snap.docs.length - 1];
        setLoading(false);
        setError(null);
      },
      (e) => {
        setError(e.message);
        setLoading(false);
      },
    );
    return unsub;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [path, depsKey, page, orderField, dir, pageSize]);

  const hasPrev = page > 0;
  const hasNext = total !== null ? (page + 1) * pageSize < total : items.length === pageSize;
  const next = useCallback(() => setPage((p) => p + 1), []);
  const prev = useCallback(() => setPage((p) => Math.max(0, p - 1)), []);

  return { items, page, pageSize, total, loading, error, hasPrev, hasNext, next, prev };
}

export function useDocument<T extends WithId>(
  path: string,
  id: string | null | undefined,
): { data: T | null; loading: boolean; error: string | null } {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!id) {
      setData(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    const unsub = onSnapshot(
      doc(db, path, id),
      (snap) => {
        setData(snap.exists() ? ({ id: snap.id, ...(snap.data() as object) } as T) : null);
        setLoading(false);
        setError(null);
      },
      (err) => {
        setError(err.message);
        setLoading(false);
      },
    );
    return unsub;
  }, [path, id]);

  return { data, loading, error };
}
